import { getMonsterFactions, getMonsterFaction, filterByFaction, getPrimaryFaction, isEncounterCoherent, FACTIONS, pickTactic, pickPattern, getPatternRoles, getTacticsForType } from '../data/factions';
import { getMonsterDetails } from '../services/monsterService';
import { clampDmPartySize, clampDmPartyLevel } from './dmPartyDefaults';

// =============================================================================
// ENCOUNTER GENERATION CONFIGURATION
// Tweak these values to adjust encounter generation behavior
// =============================================================================

// --- General Settings ---
const GENERAL_CONFIG = {
  // XP budget targeting (as ratio of target XP)
  TARGET_XP_MIN: 0.85,           // Minimum acceptable XP (85% of target)
  TARGET_XP_MAX: 1.15,           // Maximum ideal XP (115% of target)
  FALLBACK_XP_MIN: 0.75,         // Fallback minimum if ideal not achievable
  FALLBACK_XP_MAX: 1.25,         // Fallback maximum if ideal not achievable
  
  // Monster filtering
  MAX_SINGLE_MONSTER_XP: 1.3,    // Max XP for any single monster (130% of budget)
  
  // Faction filtering
  MIN_FACTION_POOL_SIZE: 3,      // Minimum monsters needed to use faction filtering
};

// --- Tactical Pattern Settings ---
const TACTIC_CONFIG = {
  // Role bias multipliers (higher = more likely to select)
  CORE_BIAS: 3.5,                // Core roles define the encounter - almost required
  SUPPORT_BIAS: 2.0,             // Support roles enhance the core - try to include
  FLEX_BIAS: 1.3,                // Flex roles fill space - optional variety
  
  PREFERRED_TACTIC_CHANCE: 0.7,  // Chance to use faction's preferred tactic
  
  // Scoring weights for tactic matching
  CORE_SCORE: 5,                 // Points per core role present
  SUPPORT_SCORE: 3,              // Points per support role present
  FLEX_SCORE: 1,                 // Points per flex role present
  MISSING_CORE_PENALTY: 0.4,     // Multiply score by this if no core roles present
  
  // Match quality thresholds (ratio of score/maxScore)
  STRONG_MATCH: 0.75,            // >= 75% = strong match
  ACCEPTABLE_MATCH: 0.5,         // >= 50% = acceptable match (minimum to label)
};

// --- Boss Encounter Settings ---
const BOSS_CONFIG = {
  // Boss selection thresholds (as ratio of target XP)
  BOSS_MIN_XP: 0.4,              // Boss must be at least 40% of budget
  BOSS_MAX_XP: 1.15,             // Boss can't exceed 115% of budget solo
  SOLO_BOSS_MIN_XP: 0.85,        // Solo boss needs at least 85% of budget
  
  // Minion settings
  MINION_MAX_XP_RATIO: 0.75,     // Minions must be <75% of boss XP
  MAX_MINIONS: 5,                // Maximum minions (total encounter size = 6)
  FALLBACK_BOSS_CANDIDATES: 5,   // How many bosses to consider in fallback
};

// --- Ambush Encounter Settings ---
const AMBUSH_CONFIG = {
  // Target monster count
  TARGET_COUNT: 4,               // Ideal number of monsters
  MIN_COUNT: 3,                  // Minimum monsters for valid encounter
  MAX_COUNT: 6,                  // Maximum monsters allowed
  
  // Monster XP range (as ratio of ideal XP per monster)
  CANDIDATE_XP_MIN: 0.3,         // Min XP ratio for candidates
  CANDIDATE_XP_MAX: 3.0,         // Max XP ratio for candidates
  
  // Composition
  HOMOGENEOUS_CHANCE: 0.2,       // Chance of single monster type (vs mixed)
  SECONDARY_CHANCE: 0.8,         // Chance to add secondary monster type
  MAX_SECONDARY: 2,              // Max secondary monsters in mixed encounter
};

// --- Waves Encounter Settings ---
const WAVES_CONFIG = {
  // Target monster count
  MIN_COUNT: 6,                  // Minimum monsters for valid encounter
  MAX_COUNT: 12,                 // Maximum monsters allowed
  
  // Composition
  VARIETY_CHANCE: 0.1,           // Chance to add a secondary monster type (10%)
  MIN_FACTION_CANDIDATES: 2,     // Minimum faction candidates to use faction filtering
};

const ENCOUNTER_TYPE_RULES = {
  boss: {
    minCount: 1,
    maxCount: 6,
    requirePrimaryBoss: true,
    bossMinXpRatio: 0.55,      // Boss must carry most of encounter XP
    minionsMaxShare: 0.45,     // Non-boss monsters can't exceed 45% combined XP
    preferredDistinctTypes: { min: 1, max: 3, ideal: 2 },
    hardMaxDistinctTypes: 4,
    preferredSoloShare: 0.5,    // target split between solo boss and boss+minions
  },
  ambush: {
    minCount: 3,
    maxCount: 6,
    requirePrimaryBoss: false,
    maxSingleMonsterShare: 0.45,
    preferredDistinctTypes: { min: 1, max: 2, ideal: 1 },
    hardMaxDistinctTypes: 3,
  },
  waves: {
    minCount: 6,
    maxCount: 12,
    requirePrimaryBoss: false,
    maxSingleMonsterShare: 0.30,
    preferRepeatableLowMidXp: true,
    preferredDistinctTypes: { min: 1, max: 2, ideal: 1 },
    hardMaxDistinctTypes: 3,
  },
};

// =============================================================================
// END CONFIGURATION
// =============================================================================

const XP_THRESHOLDS = {
  1: { easy: 25, medium: 50, hard: 75, deadly: 100 },
  2: { easy: 50, medium: 100, hard: 150, deadly: 200 },
  3: { easy: 75, medium: 150, hard: 225, deadly: 400 },
  4: { easy: 125, medium: 250, hard: 375, deadly: 500 },
  5: { easy: 250, medium: 500, hard: 750, deadly: 1100 },
  6: { easy: 300, medium: 600, hard: 900, deadly: 1400 },
  7: { easy: 350, medium: 750, hard: 1100, deadly: 1700 },
  8: { easy: 450, medium: 900, hard: 1400, deadly: 2100 },
  9: { easy: 550, medium: 1100, hard: 1600, deadly: 2400 },
  10: { easy: 600, medium: 1200, hard: 1900, deadly: 2800 },
  11: { easy: 650, medium: 1300, hard: 2000, deadly: 3100 },
  12: { easy: 700, medium: 1400, hard: 2100, deadly: 3400 },
  13: { easy: 750, medium: 1500, hard: 2300, deadly: 3700 },
  14: { easy: 850, medium: 1700, hard: 2500, deadly: 4000 },
  15: { easy: 900, medium: 1800, hard: 2800, deadly: 4500 },
  16: { easy: 1000, medium: 2000, hard: 3000, deadly: 4800 },
  17: { easy: 1100, medium: 2200, hard: 3300, deadly: 5200 },
  18: { easy: 1200, medium: 2400, hard: 3600, deadly: 5700 },
  19: { easy: 1250, medium: 2500, hard: 3800, deadly: 6100 },
  20: { easy: 1400, medium: 2800, hard: 4200, deadly: 6800 },
};

function getMonsterMultiplier(count) {
  if (count === 1) return 1;
  if (count === 2) return 1.5;
  if (count <= 6) return 2;
  if (count <= 10) return 2.5;
  if (count <= 14) return 3;
  return 4;
}

function adjustMultiplierForParty(baseMultiplier, partySize) {
  if (partySize < 3) return baseMultiplier * 1.5;
  if (partySize > 5) return baseMultiplier * 0.75;
  return baseMultiplier;
}

export function calculateEncounterBudget(levels, difficulty) {
  return levels.reduce((total, level) => {
    const clampedLevel = Math.min(20, Math.max(1, level));
    const thresholds = XP_THRESHOLDS[clampedLevel];
    return total + thresholds[difficulty];
  }, 0);
}

const ENCOUNTER_DIFFICULTY_ORDER = ['easy', 'medium', 'hard', 'deadly'];

/** Sum XP threshold for the next step above `difficulty` (e.g. medium budget when difficulty is easy). */
function getNextDifficultyBudget(levels, difficulty) {
  const i = ENCOUNTER_DIFFICULTY_ORDER.indexOf(difficulty);
  if (i < 0 || i >= ENCOUNTER_DIFFICULTY_ORDER.length - 1) return null;
  return calculateEncounterBudget(levels, ENCOUNTER_DIFFICULTY_ORDER[i + 1]);
}

export function getEncounterDifficulty(adjustedXP, levels) {
  const thresholds = {
    easy: calculateEncounterBudget(levels, 'easy'),
    medium: calculateEncounterBudget(levels, 'medium'),
    hard: calculateEncounterBudget(levels, 'hard'),
    deadly: calculateEncounterBudget(levels, 'deadly'),
  };
  
  let difficulty = 'trivial';
  let progress = 0;
  
  if (adjustedXP >= thresholds.deadly) {
    difficulty = 'deadly';
    progress = 100;
  } else if (adjustedXP >= thresholds.hard) {
    difficulty = 'hard';
    const range = thresholds.deadly - thresholds.hard;
    progress = 75 + ((adjustedXP - thresholds.hard) / range) * 25;
  } else if (adjustedXP >= thresholds.medium) {
    difficulty = 'medium';
    const range = thresholds.hard - thresholds.medium;
    progress = 50 + ((adjustedXP - thresholds.medium) / range) * 25;
  } else if (adjustedXP >= thresholds.easy) {
    difficulty = 'easy';
    const range = thresholds.medium - thresholds.easy;
    progress = 25 + ((adjustedXP - thresholds.easy) / range) * 25;
  } else {
    difficulty = 'trivial';
    progress = (adjustedXP / thresholds.easy) * 25;
  }
  
  return { difficulty, progress: Math.min(100, Math.max(0, progress)), thresholds };
}

export function calculateAdjustedXP(monsterXPs, partySize) {
  if (monsterXPs.length === 0) return 0;
  const baseXP = monsterXPs.reduce((a, b) => a + b, 0);
  const multiplier = getMonsterMultiplier(monsterXPs.length);
  const adjusted = adjustMultiplierForParty(multiplier, partySize);
  return Math.floor(baseXP * adjusted);
}

/**
 * Levels of friendly tokens that count toward encounter math: Player characters (isPc !== false)
 * with level 1–20. Legacy coins without isPc are treated as players.
 */
export function getFriendlyLevelsForEncounterDifficulty(coins) {
  if (!coins?.length) return [];
  return coins
    .filter(
      (c) =>
        !c.isEnemy &&
        c.isPc !== false &&
        typeof c.level === 'number' &&
        c.level >= 1 &&
        c.level <= 20
    )
    .map((c) => c.level);
}

/** Combined medium encounter XP budget for the given per-character levels (DMG thresholds). */
export function getMediumTargetXpForLevels(levels) {
  if (!levels?.length) return null;
  return calculateEncounterBudget(levels, 'medium');
}

/** True if every enemy token has an SRD index with a defined XP value. Vacuously true when there are no enemies. */
export function allEnemyTokensAreSrd(coins) {
  if (!coins?.length) return true;
  const enemies = coins.filter((c) => c.isEnemy);
  if (enemies.length === 0) return true;
  return enemies.every((c) => {
    const idx = c.monsterData?.index;
    if (!idx) return false;
    const m = getMonsterDetails(idx);
    return m != null && m.xp != null;
  });
}

/**
 * Live encounter difficulty for the token panel: party from player leveled friendlies, XP from SRD enemies.
 * Use {@link allEnemyTokensAreSrd} before showing the widget when any enemy exists.
 *
 * @param {object[]|null|undefined} coins
 * @param {{ partySize?: number, partyLevel?: number } | null} [partyFallback] When no PC leveled friendlies exist, use this size/level (DM defaults / encounter generator).
 */
export function getLiveEncounterDifficultyFromCoins(coins, partyFallback = null) {
  let levels = getFriendlyLevelsForEncounterDifficulty(coins);
  let usingPartyFallback = false;
  if (levels.length === 0 && partyFallback != null) {
    const ps = clampDmPartySize(partyFallback.partySize);
    const pl = clampDmPartyLevel(partyFallback.partyLevel);
    levels = Array(ps).fill(pl);
    usingPartyFallback = true;
  }

  const enemies = coins?.filter((c) => c.isEnemy) ?? [];
  const enemyXps = [];
  for (const c of enemies) {
    const idx = c.monsterData?.index;
    if (!idx) continue;
    const m = getMonsterDetails(idx);
    if (m?.xp != null) enemyXps.push(m.xp);
  }

  if (levels.length === 0) {
    return {
      ready: false,
      usingPartyFallback: false,
      levels: [],
      mediumBudget: null,
      adjustedXP: 0,
      difficulty: null,
      progress: 0,
      thresholds: null,
      enemyCount: enemies.length,
    };
  }

  const adjustedXP = enemyXps.length === 0 ? 0 : calculateAdjustedXP(enemyXps, levels.length);
  const { difficulty, progress, thresholds } = getEncounterDifficulty(adjustedXP, levels);
  return {
    ready: true,
    usingPartyFallback,
    levels,
    enemyCount: enemies.length,
    adjustedXP,
    difficulty,
    progress,
    thresholds,
    mediumBudget: getMediumTargetXpForLevels(levels),
  };
}

/**
 * Default wave size is {@link WAVES_CONFIG.MIN_COUNT}, but with low party level the
 * smallest monsters in the pool overshoot the target XP before that count (higher
 * per-count multipliers). Use the largest n in [4, MIN_COUNT] whose all-cheapest stack
 * stays within the ideal XP ceiling so generation and validation can agree.
 */
function getEffectiveWavesMinCount(levels, requestedDifficulty, monsterPool) {
  if (!monsterPool?.length) return WAVES_CONFIG.MIN_COUNT;
  const targetXP = calculateEncounterBudget(levels, requestedDifficulty);
  const partySize = levels.length;
  const xpVals = monsterPool.map((m) => m.xp).filter((x) => x > 0);
  if (!xpVals.length) return WAVES_CONFIG.MIN_COUNT;
  const cheapestXp = Math.min(...xpVals);
  const maxAdj = targetXP * GENERAL_CONFIG.TARGET_XP_MAX;

  for (let n = WAVES_CONFIG.MIN_COUNT; n >= 4; n--) {
    const adj = calculateAdjustedXP(Array(n).fill(cheapestXp), partySize);
    if (adj <= maxAdj) return n;
  }
  return 4;
}

export function getCRRangeForLevel(avgLevel) {
  // Minimum CR scales with level but always allows some low CR for waves
  // Level 1-2: CR 1/8 minimum
  // Level 3-4: CR 1/4 minimum  
  // Level 5+: CR 1/2 minimum
  let minCR;
  if (avgLevel <= 2) {
    minCR = 0.125; // CR 1/8
  } else if (avgLevel <= 4) {
    minCR = 0.25;  // CR 1/4
  } else {
    minCR = 0.5;   // CR 1/2
  }
  
  return {
    min: minCR,
    max: avgLevel + 3,
  };
}

function weightedRandomSelect(monsters) {
  if (monsters.length === 0) return null;
  
  const BASE_WEIGHT = 1;
  const weights = monsters.map(m => BASE_WEIGHT + (m.popularity || 0));
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  
  let random = Math.random() * totalWeight;
  for (let i = 0; i < monsters.length; i++) {
    random -= weights[i];
    if (random <= 0) {
      return monsters[i];
    }
  }
  
  return monsters[monsters.length - 1];
}

/**
 * Weighted random select with tiered bias toward pattern roles
 * Core = defines the fight (highest bias)
 * Support = makes the core work (medium bias)
 * Flex = fills space (light bias)
 * @param {Object[]} monsters - Array of monsters to select from
 * @param {Object|null} pattern - Tactical pattern with core/support/flex roles
 * @returns {Object|null} Selected monster
 */
function weightedRandomSelectWithRoleBias(monsters, pattern) {
  if (monsters.length === 0) return null;
  if (!pattern) return weightedRandomSelect(monsters);
  
  const coreRoles = pattern.core || [];
  const supportRoles = pattern.support || [];
  const flexRoles = pattern.flex || [];
  
  const BASE_WEIGHT = 1;
  const weights = monsters.map(m => {
    let weight = BASE_WEIGHT + (m.popularity || 0);
    
    // Apply tiered bias based on role category
    if (coreRoles.includes(m.role)) {
      weight *= TACTIC_CONFIG.CORE_BIAS;
    } else if (supportRoles.includes(m.role)) {
      weight *= TACTIC_CONFIG.SUPPORT_BIAS;
    } else if (flexRoles.includes(m.role)) {
      weight *= TACTIC_CONFIG.FLEX_BIAS;
    }
    
    return weight;
  });
  
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  let random = Math.random() * totalWeight;
  
  for (let i = 0; i < monsters.length; i++) {
    random -= weights[i];
    if (random <= 0) return monsters[i];
  }
  
  return monsters[monsters.length - 1];
}

/**
 * Apply role bias to candidate list - prioritizes core > support > flex
 * @param {Object[]} candidates - Array of candidate monsters
 * @param {Object|null} pattern - Tactical pattern
 * @returns {Object[]} Reordered candidates with core roles first, then support, then flex, then others
 */
function applyRoleBias(candidates, pattern) {
  if (!pattern || candidates.length === 0) return candidates;
  
  const coreRoles = pattern.core || [];
  const supportRoles = pattern.support || [];
  const flexRoles = pattern.flex || [];
  
  const coreMatches = candidates.filter(m => coreRoles.includes(m.role));
  const supportMatches = candidates.filter(m => supportRoles.includes(m.role) && !coreRoles.includes(m.role));
  const flexMatches = candidates.filter(m => flexRoles.includes(m.role) && !coreRoles.includes(m.role) && !supportRoles.includes(m.role));
  const others = candidates.filter(m => 
    !coreRoles.includes(m.role) && 
    !supportRoles.includes(m.role) && 
    !flexRoles.includes(m.role)
  );
  
  // Prioritize: core > support > flex > others
  return [...coreMatches, ...supportMatches, ...flexMatches, ...others];
}

/**
 * Count how many monsters have each role in an encounter
 * @param {Object[]} encounter - Array of monster objects
 * @returns {Object} Map of role -> count
 */
function getRoleCounts(encounter) {
  const counts = {};
  for (const m of encounter) {
    counts[m.role] = (counts[m.role] || 0) + 1;
  }
  return counts;
}

/**
 * Calculate how well an encounter matches a tactical pattern
 * Uses tiered scoring: core (5pts) > support (3pts) > flex (1pt)
 * Applies heavy penalty if no core roles are present
 * @param {Object[]} encounter - Array of monster objects
 * @param {Object|null} pattern - Tactical pattern
 * @returns {{ score: number, maxScore: number, ratio: number }}
 */
function scoreTacticMatch(encounter, pattern) {
  if (!pattern || encounter.length === 0) {
    return { score: 0, maxScore: 0, ratio: 0 };
  }
  
  const roleCounts = getRoleCounts(encounter);
  let score = 0;
  let maxScore = 0;
  
  // Core roles (most important)
  if (pattern.core) {
    for (const role of pattern.core) {
      maxScore += TACTIC_CONFIG.CORE_SCORE;
      if (roleCounts[role]) {
        score += TACTIC_CONFIG.CORE_SCORE;
      }
    }
  }
  
  // Support roles (medium importance)
  if (pattern.support) {
    for (const role of pattern.support) {
      maxScore += TACTIC_CONFIG.SUPPORT_SCORE;
      if (roleCounts[role]) {
        score += TACTIC_CONFIG.SUPPORT_SCORE;
      }
    }
  }
  
  // Flex roles (light bonus)
  if (pattern.flex) {
    for (const role of pattern.flex) {
      maxScore += TACTIC_CONFIG.FLEX_SCORE;
      if (roleCounts[role]) {
        score += TACTIC_CONFIG.FLEX_SCORE;
      }
    }
  }
  
  // Heavy penalty if missing ALL core roles
  const hasAnyCore = pattern.core?.some(r => roleCounts[r]);
  if (pattern.core && pattern.core.length > 0 && !hasAnyCore) {
    score *= TACTIC_CONFIG.MISSING_CORE_PENALTY;
  }
  
  return {
    score,
    maxScore,
    ratio: maxScore > 0 ? score / maxScore : 0,
  };
}

/**
 * Check if a tactic's faction requirements match the encounter
 * @param {Object} tactic - Tactic object (may have factions array)
 * @param {string|null} encounterFaction - Primary faction of the encounter
 * @returns {boolean} Whether the tactic is valid for this faction
 */
function tacticMatchesFaction(tactic, encounterFaction) {
  // No faction requirement = universal tactic
  if (!tactic.factions || tactic.factions.length === 0) return true;
  // No encounter faction = can't match faction-specific tactics
  if (!encounterFaction) return false;
  // Check if encounter faction matches any of the tactic's required factions
  return tactic.factions.includes(encounterFaction);
}

/**
 * Find the best matching tactic and pattern for an encounter
 * Checks all available tactics for the encounter type
 * Theme-specific tactics require faction match
 * @param {Object[]} encounter - Array of monster objects
 * @param {string} encounterType - 'boss', 'ambush', or 'waves'
 * @returns {{ tactic: Object|null, pattern: Object|null, score: number }}
 */
function findBestMatchingTactic(encounter, encounterType) {
  if (encounter.length === 0) return { tactic: null, pattern: null, score: 0 };
  
  const tactics = getTacticsForType(encounterType);
  const encounterFaction = getPrimaryFaction(encounter);
  
  let bestTactic = null;
  let bestPattern = null;
  let bestScore = 0;
  
  for (const tactic of tactics) {
    if (!tactic.patterns) continue;
    
    // Skip faction-specific tactics if faction doesn't match
    if (!tacticMatchesFaction(tactic, encounterFaction)) continue;
    
    for (const pattern of tactic.patterns) {
      const { ratio } = scoreTacticMatch(encounter, pattern);
      if (ratio > bestScore) {
        bestScore = ratio;
        bestTactic = tactic;
        bestPattern = pattern;
      }
    }
  }
  
  // Only return a match if it meets the acceptable threshold
  if (bestScore >= TACTIC_CONFIG.ACCEPTABLE_MATCH) {
    return { tactic: bestTactic, pattern: bestPattern, score: bestScore };
  }
  
  return { tactic: null, pattern: null, score: 0 };
}

function groupMonsters(encounter) {
  const groups = {};
  for (const monster of encounter) {
    if (groups[monster.index]) {
      groups[monster.index].count++;
    } else {
      groups[monster.index] = { ...monster, count: 1 };
    }
  }
  return Object.values(groups).sort((a, b) => b.xp - a.xp);
}

function createEncounterResult(type, label, description, encounter, partySize, faction = null) {
  const totalXP = encounter.reduce((sum, m) => sum + m.xp, 0);
  const adjustedXP = calculateAdjustedXP(encounter.map(m => m.xp), partySize);
  
  // Determine the primary faction from the encounter
  let encounterFaction = faction;
  
  // If no faction provided or we want to verify, detect from monsters
  if (!encounterFaction) {
    encounterFaction = getPrimaryFaction(encounter);
  }
  
  // Only label if encounter is coherent (all monsters compatible)
  const coherent = isEncounterCoherent(encounter);
  if (!coherent) {
    encounterFaction = null;
  }
  
  // Find the best matching tactic for this encounter (could be any tactic, not just the one we tried)
  const { tactic, pattern } = findBestMatchingTactic(encounter, type);
  
  return {
    type,
    label,
    description,
    monsters: groupMonsters(encounter),
    totalXP,
    adjustedXP,
    faction: encounterFaction,
    factionLabel: encounterFaction ? FACTIONS[encounterFaction]?.label : null,
    tactic: pattern?.name || null,
    tacticDescription: tactic?.description || null,
  };
}

function pickFactionAndFilter(monsters, minPoolSize = GENERAL_CONFIG.MIN_FACTION_POOL_SIZE) {
  if (monsters.length === 0) return { monsters: [], faction: null };
  
  // Pick a random starting monster weighted by popularity
  const seed = weightedRandomSelect(monsters);
  const factions = getMonsterFactions(seed);
  
  // If no factions, return all monsters with factions (exclude factionless)
  if (factions.length === 0) {
    const withFactions = monsters.filter(m => getMonsterFactions(m).length > 0);
    if (withFactions.length >= minPoolSize) {
      return pickFactionAndFilter(withFactions, minPoolSize);
    }
    return { monsters, faction: null };
  }
  
  // Pick one of the seed's factions randomly
  const faction = factions[Math.floor(Math.random() * factions.length)];
  
  // Filter to faction-compatible monsters (includes allies)
  const filtered = filterByFaction(monsters, faction);
  
  // If filtering leaves too few monsters, fall back to all
  if (filtered.length < minPoolSize) {
    return { monsters, faction: null };
  }
  
  return { monsters: filtered, faction };
}

function isInBudget(encounter, targetXP, partySize, minRatio = 0.5, maxRatio = 1.5) {
  const adjXP = calculateAdjustedXP(encounter.map(m => m.xp), partySize);
  return adjXP >= targetXP * minRatio && adjXP <= targetXP * maxRatio;
}

function generateBossEncounter(monsters, levels, difficulty) {
  if (monsters.length === 0) return null;
  
  const partySize = levels.length;
  const targetXP = calculateEncounterBudget(levels, difficulty);
  
  // Pick a faction first, then filter monsters
  const { monsters: factionMonsters, faction } = pickFactionAndFilter(monsters);
  
  // Pick a tactic and pattern for this encounter
  const tactic = pickTactic(faction, 'boss', weightedRandomSelect);
  const pattern = tactic ? pickPattern(tactic) : null;
  
  // Filter out monsters that massively exceed budget
  const maxBossXP = targetXP * GENERAL_CONFIG.MAX_SINGLE_MONSTER_XP;
  const budgetFilteredMonsters = factionMonsters.filter(m => m.xp <= maxBossXP);
  
  if (budgetFilteredMonsters.length === 0) return null;
  
  // Sort monsters by XP
  const sortedByXPAsc = [...budgetFilteredMonsters].sort((a, b) => a.xp - b.xp);
  const sortedByXPDesc = [...budgetFilteredMonsters].sort((a, b) => b.xp - a.xp);
  
  // Helpers
  const getAdjXP = (enc) => calculateAdjustedXP(enc.map(m => m.xp), partySize);
  
  const isInTargetRange = (enc) => {
    const adjXP = getAdjXP(enc);
    return adjXP >= targetXP * GENERAL_CONFIG.TARGET_XP_MIN && adjXP <= targetXP * GENERAL_CONFIG.TARGET_XP_MAX;
  };
  
  const isCloseEnough = (enc) => {
    const adjXP = getAdjXP(enc);
    return adjXP >= targetXP * GENERAL_CONFIG.FALLBACK_XP_MIN && adjXP <= targetXP * GENERAL_CONFIG.FALLBACK_XP_MAX;
  };
  
  // Find minions that can fill the budget gap (with role bias from pattern)
  const findMinions = (encounter, boss) => {
    const currentAdjXP = getAdjXP(encounter);
    if (currentAdjXP >= targetXP * GENERAL_CONFIG.TARGET_XP_MIN) return [];
    
    // Find weaker monsters that could serve as minions
    let potentialMinions = sortedByXPAsc.filter(m => {
      if (m.index === boss.index) return false;
      if (m.xp >= boss.xp * BOSS_CONFIG.MINION_MAX_XP_RATIO) return false;
      return true;
    });
    
    // Apply role bias from pattern
    potentialMinions = applyRoleBias(potentialMinions, pattern);
    
    return potentialMinions;
  };
  
  // Find best filler to get closer to target (with role bias)
  const findBestFiller = (encounter, minions) => {
    const currentAdjXP = getAdjXP(encounter);
    if (currentAdjXP >= targetXP * GENERAL_CONFIG.TARGET_XP_MIN) return null;
    
    // Filter to minions that would help without exceeding
    const validFillers = minions.filter(m => {
      const testEnc = [...encounter, m];
      const newAdjXP = getAdjXP(testEnc);
      return newAdjXP <= targetXP * GENERAL_CONFIG.FALLBACK_XP_MAX && newAdjXP > currentAdjXP;
    });
    
    if (validFillers.length === 0) return null;
    
    // Sort by how close they get us to target
    validFillers.sort((a, b) => {
      const aEnc = [...encounter, a];
      const bEnc = [...encounter, b];
      const aDiff = Math.abs(getAdjXP(aEnc) - targetXP);
      const bDiff = Math.abs(getAdjXP(bEnc) - targetXP);
      return aDiff - bDiff;
    });
    
    // Return one of the top choices with role bias
    const topFillers = validFillers.slice(0, Math.min(3, validFillers.length));
    return weightedRandomSelectWithRoleBias(topFillers, pattern);
  };
  
  const maxEncounterSize = ENCOUNTER_TYPE_RULES.boss.maxCount;
  
  // Find boss candidates - strong monsters that can hit budget solo OR with minions
  // Apply role bias to favor pattern's core roles
  let bossCandidates = sortedByXPDesc.filter(m => {
    const soloXP = calculateAdjustedXP([m.xp], partySize);
    return soloXP >= targetXP * BOSS_CONFIG.BOSS_MIN_XP && soloXP <= targetXP * BOSS_CONFIG.BOSS_MAX_XP;
  });
  bossCandidates = applyRoleBias(bossCandidates, pattern);
  
  // Separate solo-capable bosses (can hit target alone) from those needing minions
  let soloBosses = bossCandidates.filter(m => {
    const soloXP = calculateAdjustedXP([m.xp], partySize);
    return soloXP >= targetXP * BOSS_CONFIG.SOLO_BOSS_MIN_XP;
  });
  soloBosses = applyRoleBias(soloBosses, pattern);
  
  // If no good boss candidates, use strongest available
  const finalBossCandidates = bossCandidates.length > 0 
    ? bossCandidates 
    : sortedByXPDesc.slice(0, Math.min(BOSS_CONFIG.FALLBACK_BOSS_CANDIDATES, sortedByXPDesc.length));
  
  if (finalBossCandidates.length === 0) return null;
  
  // PRIORITY 1: Try solo bosses first (single powerful enemy)
  if (soloBosses.length > 0) {
    const boss = weightedRandomSelectWithRoleBias(soloBosses, pattern);
    const bossFaction = getMonsterFaction(boss);
    return createEncounterResult('boss', 'Boss Fight', 'A single powerful enemy', [boss], partySize, bossFaction || faction);
  }
  
  // PRIORITY 2: No solo bosses available - use boss + minions
  for (let i = 0; i < Math.min(3, finalBossCandidates.length); i++) {
    const boss = i === 0 ? weightedRandomSelectWithRoleBias(finalBossCandidates, pattern) : finalBossCandidates[i];
    const bossFaction = getMonsterFaction(boss);
    const encounter = [boss];
    
    // Boss is under budget - add minions
    const minions = findMinions(encounter, boss);
    
    if (minions.length > 0) {
      let attempts = 0;
      while (attempts < 20 && encounter.length < maxEncounterSize) {
        attempts++;
        
        if (isInTargetRange(encounter)) break;
        
        const minion = findBestFiller(encounter, minions);
        if (!minion) break;
        
        encounter.push(minion);
      }
    }
    
    // Check if this encounter works
    if (isInTargetRange(encounter) || isCloseEnough(encounter)) {
      const desc = encounter.length > 1 ? 'A powerful enemy with minions' : 'A single powerful enemy';
      return createEncounterResult('boss', 'Boss Fight', desc, encounter, partySize, bossFaction || faction);
    }
  }
  
  // FALLBACK: Just use strongest boss + whatever minions we can add
  const fallbackBoss = weightedRandomSelectWithRoleBias(finalBossCandidates.slice(0, 3), pattern);
  if (fallbackBoss) {
    const encounter = [fallbackBoss];
    const minions = findMinions(encounter, fallbackBoss);
    
    let attempts = 0;
    while (attempts < 20 && encounter.length < maxEncounterSize && minions.length > 0) {
      attempts++;
      
      const minion = findBestFiller(encounter, minions);
      if (!minion) break;
      
      encounter.push(minion);
      
      if (isCloseEnough(encounter)) break;
    }
    
    const desc = encounter.length > 1 ? 'A powerful enemy with minions' : 'A single powerful enemy';
    return createEncounterResult('boss', 'Boss Fight', desc, encounter, partySize, getMonsterFaction(fallbackBoss) || faction);
  }
  
  // LAST RESORT: Just pick the strongest monster from original list
  if (monsters.length > 0) {
    const anyBoss = [...monsters].sort((a, b) => b.xp - a.xp)[0];
    return createEncounterResult('boss', 'Boss Fight', 'A powerful enemy', [anyBoss], partySize, null);
  }
  
  return null;
}

function generateAmbushEncounter(monsters, levels, difficulty) {
  if (monsters.length === 0) return null;
  
  const partySize = levels.length;
  const targetXP = calculateEncounterBudget(levels, difficulty);
  const nextTierBudget = getNextDifficultyBudget(levels, difficulty);
  
  // Pick a faction first, then filter monsters
  const { monsters: factionMonsters, faction } = pickFactionAndFilter(monsters);
  
  // Pick a tactic and pattern for this encounter
  const tactic = pickTactic(faction, 'ambush', weightedRandomSelect);
  const pattern = tactic ? pickPattern(tactic) : null;
  
  // Filter out monsters that would exceed budget on their own
  const maxSingleMonsterXP = targetXP * GENERAL_CONFIG.MAX_SINGLE_MONSTER_XP;
  const budgetFilteredMonsters = factionMonsters.filter(m => m.xp <= maxSingleMonsterXP);
  
  if (budgetFilteredMonsters.length === 0) return null;
  
  // Sort all budget-valid monsters by XP for gap-filling later
  const sortedByXP = [...budgetFilteredMonsters].sort((a, b) => a.xp - b.xp);
  
  // For ambush, target configurable monster count
  const baseMultiplier = getMonsterMultiplier(AMBUSH_CONFIG.TARGET_COUNT);
  const adjustedMultiplier = adjustMultiplierForParty(baseMultiplier, partySize);
  const idealRawXPTotal = targetXP / adjustedMultiplier;
  const idealXPPerMonster = idealRawXPTotal / AMBUSH_CONFIG.TARGET_COUNT;
  
  // Find primary candidates in appropriate XP range
  let candidates = budgetFilteredMonsters.filter(m => 
    m.xp >= idealXPPerMonster * AMBUSH_CONFIG.CANDIDATE_XP_MIN && 
    m.xp <= idealXPPerMonster * AMBUSH_CONFIG.CANDIDATE_XP_MAX
  );
  
  if (candidates.length < 3) {
    candidates = [...budgetFilteredMonsters].sort((a, b) => {
      const aDiff = Math.abs(a.xp - idealXPPerMonster);
      const bDiff = Math.abs(b.xp - idealXPPerMonster);
      return aDiff - bDiff;
    }).slice(0, Math.min(10, budgetFilteredMonsters.length));
  }
  
  if (candidates.length === 0) return null;
  
  // Apply tactical pattern role bias to candidates
  candidates = applyRoleBias(candidates, pattern);
  
  // Helpers
  const getAdjXP = (enc) => calculateAdjustedXP(enc.map(m => m.xp), partySize);
  
  const isInTargetRange = (enc) => {
    const adjXP = getAdjXP(enc);
    if (
      adjXP >= targetXP * GENERAL_CONFIG.TARGET_XP_MIN &&
      adjXP <= targetXP * GENERAL_CONFIG.TARGET_XP_MAX
    ) {
      return true;
    }
    const { difficulty: tier } = getEncounterDifficulty(adjXP, levels);
    return tier === difficulty;
  };
  
  const isCloseEnough = (enc) => {
    const adjXP = getAdjXP(enc);
    if (adjXP < targetXP * GENERAL_CONFIG.FALLBACK_XP_MIN) return false;
    if (nextTierBudget != null) return adjXP < nextTierBudget;
    return adjXP <= targetXP * GENERAL_CONFIG.FALLBACK_XP_MAX;
  };
  
  const wouldExceedBudget = (enc, monster) => {
    const adj = getAdjXP([...enc, monster]);
    if (nextTierBudget != null) return adj >= nextTierBudget;
    return adj > targetXP * GENERAL_CONFIG.FALLBACK_XP_MAX;
  };
  
  // Find a monster that fits the remaining budget gap (with role bias)
  const findGapFiller = (enc) => {
    const currentAdjXP = getAdjXP(enc);
    const gap = targetXP - currentAdjXP;
    
    if (gap <= 0) return null;
    
    // Find monsters that would bring us closer to target without exceeding
    const fillers = sortedByXP.filter(m => {
      const testEnc = [...enc, m];
      const newAdjXP = getAdjXP(testEnc);
      const okCap =
        nextTierBudget != null
          ? newAdjXP < nextTierBudget
          : newAdjXP <= targetXP * GENERAL_CONFIG.FALLBACK_XP_MAX;
      return okCap && newAdjXP > currentAdjXP;
    });
    
    if (fillers.length === 0) return null;
    
    // Prefer monsters that get us closest to target
    fillers.sort((a, b) => {
      const aEnc = [...enc, a];
      const bEnc = [...enc, b];
      const aDiff = Math.abs(getAdjXP(aEnc) - targetXP);
      const bDiff = Math.abs(getAdjXP(bEnc) - targetXP);
      return aDiff - bDiff;
    });
    
    // Return best filler with role bias
    const topFillers = fillers.slice(0, Math.min(3, fillers.length));
    return weightedRandomSelectWithRoleBias(topFillers, pattern);
  };
  
  // STRATEGY: Pick 1-2 monster types and fill with multiples
  const useHomogeneous = Math.random() < AMBUSH_CONFIG.HOMOGENEOUS_CHANCE;
  const encounter = [];
  
  if (useHomogeneous) {
    const primary = weightedRandomSelectWithRoleBias(candidates, pattern);
    
    // Add primary monsters until we can't add more without exceeding
    while (encounter.length < AMBUSH_CONFIG.MAX_COUNT) {
      if (encounter.length > 0 && wouldExceedBudget(encounter, primary)) {
        break;
      }
      encounter.push(primary);
      
      if (encounter.length >= AMBUSH_CONFIG.MIN_COUNT && isInTargetRange(encounter)) {
        return createEncounterResult('ambush', 'Ambush', 'A coordinated group attack', encounter, partySize, faction);
      }
    }
  } else {
    const primary = weightedRandomSelectWithRoleBias(candidates, pattern);
    const secondaryCandidates = candidates.filter(m => m.index !== primary.index);
    const secondary = secondaryCandidates.length > 0 ? weightedRandomSelectWithRoleBias(secondaryCandidates, pattern) : primary;
    
    let secondaryCount = 0;
    
    while (encounter.length < AMBUSH_CONFIG.MAX_COUNT) {
      const nextMonster = (encounter.length >= 2 && secondaryCount < AMBUSH_CONFIG.MAX_SECONDARY && Math.random() < AMBUSH_CONFIG.SECONDARY_CHANCE) ? secondary : primary;
      
      if (encounter.length > 0 && wouldExceedBudget(encounter, nextMonster)) {
        break;
      }
      
      encounter.push(nextMonster);
      if (nextMonster === secondary) secondaryCount++;
      
      if (encounter.length >= AMBUSH_CONFIG.MIN_COUNT && isInTargetRange(encounter)) {
        return createEncounterResult('ambush', 'Ambush', 'A coordinated group attack', encounter, partySize, faction);
      }
    }
  }
  
  // GAP FILLING: If we're under budget, try to add smaller monsters to fill the gap
  if (encounter.length >= AMBUSH_CONFIG.MIN_COUNT && !isInTargetRange(encounter)) {
    let attempts = 0;
    while (attempts < 10 && encounter.length < AMBUSH_CONFIG.MAX_COUNT) {
      attempts++;
      const filler = findGapFiller(encounter);
      if (!filler) break;
      
      encounter.push(filler);
      
      if (isInTargetRange(encounter)) {
        return createEncounterResult('ambush', 'Ambush', 'A coordinated group attack', encounter, partySize, faction);
      }
    }
  }
  
  // Accept if close enough
  if (encounter.length >= AMBUSH_CONFIG.MIN_COUNT && isCloseEnough(encounter)) {
    return createEncounterResult('ambush', 'Ambush', 'A coordinated group attack', encounter, partySize, faction);
  }
  
  // FALLBACK: Start fresh with a different approach - try to hit target exactly
  const fallbackEncounter = [];
  const fallbackPrimary = weightedRandomSelectWithRoleBias(candidates, pattern);
  
  while (fallbackEncounter.length < AMBUSH_CONFIG.MAX_COUNT) {
    if (fallbackEncounter.length > 0 && wouldExceedBudget(fallbackEncounter, fallbackPrimary)) {
      const filler = findGapFiller(fallbackEncounter);
      if (filler) {
        fallbackEncounter.push(filler);
      } else {
        break;
      }
    } else {
      fallbackEncounter.push(fallbackPrimary);
    }
    
    if (fallbackEncounter.length >= AMBUSH_CONFIG.MIN_COUNT && isCloseEnough(fallbackEncounter)) {
      return createEncounterResult('ambush', 'Ambush', 'A coordinated group attack', fallbackEncounter, partySize, faction);
    }
  }
  
  if (fallbackEncounter.length >= AMBUSH_CONFIG.MIN_COUNT) {
    return createEncounterResult('ambush', 'Ambush', 'A coordinated group attack', fallbackEncounter, partySize, faction);
  }
  
  // LAST RESORT: Just pick 2-4 random monsters from original list
  if (monsters.length >= AMBUSH_CONFIG.MIN_COUNT) {
    const shuffled = [...monsters].sort(() => Math.random() - 0.5);
    const count = Math.min(AMBUSH_CONFIG.TARGET_COUNT, shuffled.length);
    const lastResort = shuffled.slice(0, count);
    return createEncounterResult('ambush', 'Ambush', 'A coordinated group attack', lastResort, partySize, null);
  }
  
  return null;
}

function generateWavesEncounter(monsters, levels, difficulty) {
  if (monsters.length === 0) return null;
  
  const partySize = levels.length;
  const targetXP = calculateEncounterBudget(levels, difficulty);
  const wavesMinCount = getEffectiveWavesMinCount(levels, difficulty, monsters);
  
  // Helpers
  const getAdjXP = (enc) => calculateAdjustedXP(enc.map(m => m.xp), partySize);
  
  // Sort all monsters by XP ascending - use ALL monsters, filter later
  const sortedMonsters = [...monsters].filter(m => m.xp > 0).sort((a, b) => a.xp - b.xp);
  if (sortedMonsters.length === 0) return null;
  
  // For waves, we just need to find monsters cheap enough to stack 4+ of them
  // Maximum XP per monster = target / (MIN_COUNT * multiplier_for_min_count)
  // For 4 monsters, multiplier is 2.0, so max XP = target / 8
  // But let's be generous and allow up to target / 4 (allows some exceeding)
  const maxMonsterXP = targetXP / wavesMinCount;
  
  // Find all monsters we could use for a wave
  const waveEligible = sortedMonsters.filter(m => m.xp <= maxMonsterXP);
  
  // If no monsters are cheap enough, use the cheapest available
  const candidates = waveEligible.length > 0 ? waveEligible : sortedMonsters.slice(0, 10);
  
  if (candidates.length === 0) return null;
  
  // Try faction filtering but don't be strict about it
  let faction = null;
  let finalCandidates = candidates;
  
  const { monsters: factionMonsters, faction: pickedFaction } = pickFactionAndFilter(candidates);
  if (factionMonsters.length >= WAVES_CONFIG.MIN_FACTION_CANDIDATES) {
    finalCandidates = factionMonsters;
    faction = pickedFaction;
  }
  
  // Pick a tactic (optional)
  const tactic = pickTactic(faction, 'waves', weightedRandomSelect);
  const pattern = tactic ? pickPattern(tactic) : null;
  
  // Pick primary monster
  const primary = weightedRandomSelectWithRoleBias(finalCandidates, pattern);
  
  // Build encounter: stack primary until we hit target range
  const encounter = [];
  while (encounter.length < WAVES_CONFIG.MAX_COUNT) {
    // Check what happens if we add another monster
    const testEnc = [...encounter, primary];
    const testAdjXP = getAdjXP(testEnc);
    
    // If adding this monster would exceed 115% of target and we already have enough, stop
    if (encounter.length >= wavesMinCount && testAdjXP > targetXP * GENERAL_CONFIG.TARGET_XP_MAX) {
      break;
    }
    
    // Add the monster
    encounter.push(primary);
    
    // Check if we're now in good range
    const adjXP = getAdjXP(encounter);
    if (encounter.length >= wavesMinCount && 
        adjXP >= targetXP * GENERAL_CONFIG.TARGET_XP_MIN && 
        adjXP <= targetXP * GENERAL_CONFIG.TARGET_XP_MAX) {
      break;
    }
  }
  
  // Rarely add variety (waves are mostly homogeneous hordes)
  if (encounter.length >= wavesMinCount && finalCandidates.length > 1 && Math.random() < WAVES_CONFIG.VARIETY_CHANCE) {
    const others = finalCandidates.filter(m => m.index !== primary.index);
    if (others.length > 0) {
      const secondary = weightedRandomSelectWithRoleBias(others, pattern);
      // Only replace 1 monster
      const idx = Math.floor(Math.random() * encounter.length);
      encounter[idx] = secondary;
    }
  }
  
  // Accept if we have minimum count
  if (encounter.length >= wavesMinCount) {
    return createEncounterResult('waves', 'Waves', 'A horde of weaker enemies', encounter, partySize, faction);
  }
  
  // Ultra fallback: stack cheapest monster until we hit target range
  const cheapest = sortedMonsters[0];
  const fallback = [];
  while (fallback.length < WAVES_CONFIG.MAX_COUNT) {
    const testEnc = [...fallback, cheapest];
    const testAdjXP = getAdjXP(testEnc);
    
    // Stop if adding would exceed target max and we have enough
    if (fallback.length >= wavesMinCount && testAdjXP > targetXP * GENERAL_CONFIG.TARGET_XP_MAX) {
      break;
    }
    
    fallback.push(cheapest);
    
    // Stop if we're in target range
    const adjXP = getAdjXP(fallback);
    if (fallback.length >= wavesMinCount && 
        adjXP >= targetXP * GENERAL_CONFIG.TARGET_XP_MIN) {
      break;
    }
  }
  
  if (fallback.length >= wavesMinCount) {
    return createEncounterResult('waves', 'Waves', 'A horde of weaker enemies', fallback, partySize, null);
  }
  
  // Absolute last resort: just return wavesMinCount of the cheapest monster
  const lastResort = Array(wavesMinCount).fill(cheapest);
  return createEncounterResult('waves', 'Waves', 'A horde of weaker enemies', lastResort, partySize, null);
}

export function generateEncounterOptions(monsters, levels, difficulty) {
  const boss = generateStrictEncounterOfType(monsters, levels, difficulty, 'boss');
  const ambush = generateStrictEncounterOfType(monsters, levels, difficulty, 'ambush');
  const waves = generateStrictEncounterOfType(monsters, levels, difficulty, 'waves');
  
  return {
    boss,
    ambush,
    waves,
    targetXP: calculateEncounterBudget(levels, difficulty),
  };
}

export function getEncounterKey(encounter) {
  if (!encounter || !encounter.monsters) return null;
  return encounter.monsters.map(m => `${m.index}:${m.count}`).sort().join(',');
}

function expandGroupedMonsters(groupedMonsters) {
  const expanded = [];
  for (const grouped of groupedMonsters || []) {
    const copies = Math.max(1, grouped.count || 1);
    for (let i = 0; i < copies; i++) expanded.push(grouped);
  }
  return expanded;
}

function getEncounterMonsterCount(encounter) {
  return (encounter?.monsters || []).reduce((sum, m) => sum + (m.count || 1), 0);
}

function getDistinctMonsterTypeCount(encounter) {
  return new Set((encounter?.monsters || []).map(m => m.index)).size;
}

function isSoloBossEncounter(encounter) {
  return getEncounterMonsterCount(encounter) === 1;
}

function getEncounterXpShares(encounter) {
  const entries = (encounter?.monsters || []).map(m => {
    const count = m.count || 1;
    return {
      index: m.index,
      xp: m.xp,
      count,
      totalXp: m.xp * count,
    };
  });

  const totalXp = entries.reduce((sum, e) => sum + e.totalXp, 0);
  if (totalXp <= 0) return { entries: [], maxGroupShare: 0, maxIndividualShare: 0 };

  const withShare = entries.map(e => ({ ...e, share: e.totalXp / totalXp }));
  const maxGroupShare = withShare.reduce((max, e) => Math.max(max, e.share), 0);
  const maxIndividualShare = withShare.reduce(
    (max, e) => Math.max(max, e.xp / totalXp),
    0
  );
  return { entries: withShare, maxGroupShare, maxIndividualShare };
}

function passesEncounterTypeRules(encounter, levels, requestedDifficulty, type, monsterPool) {
  const rules = ENCOUNTER_TYPE_RULES[type];
  if (!rules || !encounter?.monsters?.length) return false;

  const count = getEncounterMonsterCount(encounter);
  const distinctTypes = getDistinctMonsterTypeCount(encounter);
  const minCountRequired =
    type === 'waves' && monsterPool?.length
      ? getEffectiveWavesMinCount(levels, requestedDifficulty, monsterPool)
      : rules.minCount;
  if (count < minCountRequired || count > rules.maxCount) return false;
  if (rules.hardMaxDistinctTypes != null && distinctTypes > rules.hardMaxDistinctTypes) return false;

  const { entries, maxGroupShare, maxIndividualShare } = getEncounterXpShares(encounter);
  if (entries.length === 0) return false;

  if (rules.requirePrimaryBoss) {
    const bossShare = maxGroupShare;
    const minionsShare = 1 - bossShare;
    if (bossShare < rules.bossMinXpRatio) return false;
    if (minionsShare > rules.minionsMaxShare) return false;
  }

  // "Single monster share" means one individual creature's share, not grouped-by-type share.
  // This allows encounters like 3x the same creature while still preventing accidental solo bosses.
  if (rules.maxSingleMonsterShare != null && maxIndividualShare > rules.maxSingleMonsterShare) {
    return false;
  }

  if (rules.preferRepeatableLowMidXp) {
    const targetXP = calculateEncounterBudget(levels, requestedDifficulty);
    const capDivisor = type === 'waves' && monsterPool?.length ? minCountRequired : rules.minCount;
    const repeatableXpCap = targetXP / capDivisor;
    const lowMidCount = entries
      .filter(e => e.xp <= repeatableXpCap)
      .reduce((sum, e) => sum + e.count, 0);
    const lowMidRatio = count > 0 ? lowMidCount / count : 0;
    if (lowMidRatio < 0.6) return false;
  }

  return true;
}

function isEncounterDifficultyMatch(encounter, levels, requestedDifficulty) {
  if (!encounter || !levels?.length || !requestedDifficulty) return false;
  const adjusted = encounter.adjustedXP;
  const { difficulty: actualTier } = getEncounterDifficulty(adjusted, levels);
  if (actualTier === requestedDifficulty) return true;

  const targetXP = calculateEncounterBudget(levels, requestedDifficulty);
  return (
    adjusted >= targetXP * GENERAL_CONFIG.TARGET_XP_MIN &&
    adjusted <= targetXP * GENERAL_CONFIG.TARGET_XP_MAX
  );
}

function isEncounterValidForRequest(encounter, levels, requestedDifficulty, type, monsterPool) {
  return (
    isEncounterDifficultyMatch(encounter, levels, requestedDifficulty) &&
    passesEncounterTypeRules(encounter, levels, requestedDifficulty, type, monsterPool)
  );
}

function getEncounterTacticFit(encounter, type) {
  if (!encounter?.monsters?.length) return 0;
  const expanded = expandGroupedMonsters(encounter.monsters);
  const { score } = findBestMatchingTactic(expanded, type);
  return score || 0;
}

function getEncounterDiversityScore(encounter, selectedEncounters) {
  if (!encounter?.monsters?.length) return 1;
  if (!selectedEncounters?.length) return 1;

  const candidateIndexes = new Set(encounter.monsters.map(m => m.index));
  const candidateTactic = encounter.tactic || '';
  let totalDistance = 0;

  for (const selected of selectedEncounters) {
    const selectedIndexes = new Set((selected.monsters || []).map(m => m.index));
    const selectedTactic = selected.tactic || '';

    const union = new Set([...candidateIndexes, ...selectedIndexes]);
    let intersectionSize = 0;
    for (const idx of candidateIndexes) {
      if (selectedIndexes.has(idx)) intersectionSize++;
    }

    const jaccardDistance = union.size > 0 ? 1 - (intersectionSize / union.size) : 1;
    const tacticBonus = candidateTactic && selectedTactic && candidateTactic !== selectedTactic ? 0.2 : 0;
    totalDistance += Math.min(1, jaccardDistance + tacticBonus);
  }

  return totalDistance / selectedEncounters.length;
}

function getEncounterCompositionFit(encounter, type) {
  const rules = ENCOUNTER_TYPE_RULES[type];
  if (!rules?.preferredDistinctTypes || !encounter?.monsters?.length) return 1;

  const distinct = getDistinctMonsterTypeCount(encounter);
  const { min, max, ideal } = rules.preferredDistinctTypes;
  const center = ideal ?? min;

  if (distinct >= min && distinct <= max) {
    const spread = Math.max(1, Math.max(Math.abs(max - center), Math.abs(min - center)));
    return Math.max(0.7, 1 - (Math.abs(distinct - center) / spread) * 0.3);
  }

  const distanceOutside = distinct < min ? min - distinct : distinct - max;
  return Math.max(0, 0.6 - (distanceOutside * 0.2));
}

function getBossStructureBalanceFit(encounter, selectedEncounters) {
  const rules = ENCOUNTER_TYPE_RULES.boss;
  const preferredSoloShare = rules?.preferredSoloShare ?? 0.5;
  const candidateIsSolo = isSoloBossEncounter(encounter);

  if (!selectedEncounters?.length) return 1;

  const soloCount = selectedEncounters.filter(isSoloBossEncounter).length;
  const selectedSoloShare = soloCount / selectedEncounters.length;

  const projectedSoloCount = soloCount + (candidateIsSolo ? 1 : 0);
  const projectedShare = projectedSoloCount / (selectedEncounters.length + 1);
  const currentDistance = Math.abs(selectedSoloShare - preferredSoloShare);
  const projectedDistance = Math.abs(projectedShare - preferredSoloShare);

  if (projectedDistance < currentDistance) return 1;
  if (projectedDistance === currentDistance) return 0.7;
  return 0.25;
}

function pickRankedEncounters(candidates, count, type) {
  if (!candidates?.length || count <= 0) return [];

  const withTacticFit = candidates.map(encounter => ({
    encounter,
    tacticFit: getEncounterTacticFit(encounter, type),
  }));

  const selected = [];
  const remaining = [...withTacticFit];

  while (selected.length < count && remaining.length > 0) {
    let bestIdx = 0;
    let bestScore = -1;

    for (let i = 0; i < remaining.length; i++) {
      const candidate = remaining[i];
      const diversity = getEncounterDiversityScore(
        candidate.encounter,
        selected.map(s => s.encounter)
      );
      const compositionFit = getEncounterCompositionFit(candidate.encounter, type);
      const bossStructureFit = type === 'boss'
        ? getBossStructureBalanceFit(candidate.encounter, selected.map(s => s.encounter))
        : 0;
      const rankScore = type === 'boss'
        ? (candidate.tacticFit * 0.5) + (compositionFit * 0.2) + (diversity * 0.1) + (bossStructureFit * 0.2)
        : (candidate.tacticFit * 0.6) + (compositionFit * 0.25) + (diversity * 0.15);

      if (rankScore > bestScore) {
        bestScore = rankScore;
        bestIdx = i;
      }
    }

    selected.push(remaining.splice(bestIdx, 1)[0]);
  }

  return selected.map(s => s.encounter);
}

function generateStrictEncounterOfType(monsters, levels, difficulty, type, maxAttempts = 120) {
  const generators = {
    boss: generateBossEncounter,
    ambush: generateAmbushEncounter,
    waves: generateWavesEncounter,
  };

  const generator = generators[type];
  if (!generator) return null;

  const candidates = [];
  const seen = new Set();

  for (let i = 0; i < maxAttempts; i++) {
    const encounter = generator(monsters, levels, difficulty);
    if (!encounter || !isEncounterValidForRequest(encounter, levels, difficulty, type, monsters)) continue;

    const key = getEncounterKey(encounter);
    if (seen.has(key)) continue;
    seen.add(key);
    candidates.push(encounter);
  }

  const ranked = pickRankedEncounters(candidates, 1, type);
  return ranked[0] || null;
}

export function generateEncountersOfType(monsters, levels, difficulty, type, count = 3, existingKeys = new Set()) {
  const generators = {
    boss: generateBossEncounter,
    ambush: generateAmbushEncounter,
    waves: generateWavesEncounter,
  };
  
  const generator = generators[type];
  if (!generator) return [];
  
  // If no monsters available, return empty
  if (!monsters || monsters.length === 0) return [];
  
  const validCandidates = [];
  const seen = new Set(existingKeys);
  let attempts = 0;
  const maxAttempts = Math.max(400, count * 120);
  let consecutiveHardFailures = 0;
  const targetPoolSize = Math.max(count * 6, count + 2);
  
  while (validCandidates.length < targetPoolSize && attempts < maxAttempts) {
    attempts++;
    const encounter = generator(monsters, levels, difficulty);
    
    if (!encounter) {
      consecutiveHardFailures++;
    } else {
      const key = getEncounterKey(encounter);
      const validForRequest = isEncounterValidForRequest(encounter, levels, difficulty, type, monsters);
      if (!validForRequest) {
        consecutiveHardFailures++;
      } else if (seen.has(key)) {
        // Duplicate composition: keep trying; do not count toward early exit.
      } else {
        seen.add(key);
        validCandidates.push(encounter);
        consecutiveHardFailures = 0;
      }
    }
    
    // Only bail when the generator repeatedly returns null/invalid — not when duplicates repeat.
    if (consecutiveHardFailures > 40) break;
  }
  
  return pickRankedEncounters(validCandidates, count, type);
}

// Keep the old function for backwards compatibility
export function generateEncounter(monsters, levels, difficulty) {
  const options = generateEncounterOptions(monsters, levels, difficulty);
  return options.ambush?.monsters || options.boss?.monsters || options.waves?.monsters || [];
}
